import React from "react";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import MDSnackbar from "components/MDSnackbar";
import { useState } from "react";
import appData from "layouts/mosques/data/appData";
import { useAddMosque, useUpdateMosque } from "api/useMosques.ts";
import MDBox from "components/MDBox";
import { useMediaQuery, useTheme, FormControl, InputLabel, Select, MenuItem, InputAdornment, IconButton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

function Tables() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const addMosque = useAddMosque();
  const updateMosque = useUpdateMosque();
  const [isMosqueModalOpen, setIsMosqueModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [newMosque, setNewMosque] = useState({
    name: "",
    responsibleName: "",
    mobileNumber: "",
    secondNumber: "",
    address: "",
    city: "",
    district: "",
    postalCode: "",
    isActive: true,
    location: {
      type: "Point",
      coordinates: [0, 0],
    },
  });

  const handleEditMosque = (mosque) => {
    setIsEditMode(true);
    setNewMosque({
      _id: mosque._id,
      name: mosque.name || "",
      responsibleName: mosque.responsibleName || "",
      mobileNumber: mosque.mobileNumber || "",
      secondNumber: mosque.secondNumber || "",
      address: mosque.address || "",
      city: mosque.city || "",
      district: mosque.district || "",
      postalCode: mosque.postalCode || "",
      isActive: mosque.isActive !== undefined ? mosque.isActive : true,
      location: {
        type: mosque.location?.type || "Point",
        coordinates: mosque.location?.coordinates || [0, 0],
      },
    });
    setIsMosqueModalOpen(true);
  };

  const searchParams = {
    search: searchTerm || undefined,
    sortBy: sortBy
  };

  const { columns, rows, notification, setNotification, handleCloseNotification } =
    appData(handleEditMosque, searchParams);

  const handleAddMosqueClick = () => {
    setIsEditMode(false);
    setNewMosque({
      name: "",
      responsibleName: "",
      mobileNumber: "",
      secondNumber: "",
      address: "",
      city: "",
      district: "",
      postalCode: "",
      isActive: true,
      location: {
        type: "Point",
        coordinates: [0, 0],
      },
    });
    setIsMosqueModalOpen(true); // Open modal for adding new mosque
  };
  const handleCloseAddMosqueModal = () => {
    setIsMosqueModalOpen(false); // Close modal
    setIsEditMode(false);
    setNewMosque({
      name: "",
      responsibleName: "",
      mobileNumber: "",
      secondNumber: "",
      address: "",
      city: "",
      district: "",
      postalCode: "",
      isActive: true,
      location: {
        type: "Point",
        coordinates: [0, 0],
      },
    });
  };
  const handleInputChangeNewMosque = (e) => {
    const { name, value } = e.target;

    setNewMosque((prevState) => {
      if (name === "x" || name === "y") {
        const newCoordinates = [...prevState.location.coordinates];
        if (name === "x") {
          newCoordinates[1] = parseFloat(value) || ""; // Latitude at index 1
        } else {
          newCoordinates[0] = parseFloat(value) || ""; // Longitude at index 0
        }

        return {
          ...prevState,
          location: {
            ...prevState.location,
            coordinates: newCoordinates,
          },
        };
      } else if (name.includes(".")) {
        const [parentKey, childKey] = name.split(".");
        return {
          ...prevState,
          [parentKey]: {
            ...prevState[parentKey],
            [childKey]: value,
          },
        };
      } else {
        return {
          ...prevState,
          [name]: value,
        };
      }
    });
  };

  const handleSaveMosque = async () => {
    // Validation
    if (!newMosque.name.trim()) {
      setNotification({
        open: true,
        message: t("mosques.validation.mosqueNameRequired"),
        severity: "error",
      });
      return;
    }
    if (!newMosque.responsibleName.trim()) {
      setNotification({
        open: true,
        message: t("mosques.validation.responsibleNameRequired"),
        severity: "error",
      });
      return;
    }
    if (!newMosque.mobileNumber.trim()) {
      setNotification({
        open: true,
        message: t("mosques.validation.mobileNumberRequired"),
        severity: "error",
      });
      return;
    }
    if (!newMosque.address.trim() || newMosque.address.trim().length < 10) {
      setNotification({
        open: true,
        message: t("mosques.validation.addressRequired"),
        severity: "error",
      });
      return;
    }
    if (!newMosque.city.trim() || newMosque.city.trim().length < 2) {
      setNotification({
        open: true,
        message: t("mosques.validation.cityRequired"),
        severity: "error",
      });
      return;
    }
    if (
      newMosque.location.type !== "Point" ||
      !Array.isArray(newMosque.location.coordinates) ||
      newMosque.location.coordinates.length !== 2 ||
      newMosque.location.coordinates.some((coord) => typeof coord !== "number" || coord === 0)
    ) {
      setNotification({
        open: true,
        message: t("mosques.validation.locationRequired"),
        severity: "error",
      });
      return;
    }

    const mosqueData = {
      name: newMosque.name.trim(),
      responsibleName: newMosque.responsibleName.trim(),
      mobileNumber: newMosque.mobileNumber.trim(),
      secondNumber: newMosque.secondNumber.trim() || undefined,
      address: newMosque.address.trim(),
      city: newMosque.city.trim(),
      district: newMosque.district.trim() || undefined,
      postalCode: newMosque.postalCode.trim() || undefined,
      isActive: newMosque.isActive,
      location: {
        type: "Point",
        coordinates: newMosque.location.coordinates,
      },
    };

    if (isEditMode) {
      const updateData = { ...mosqueData, _id: newMosque._id };
      updateMosque.mutate(updateData, {
        onSuccess: () => {
          setNotification({
            open: true,
            message: t("mosques.mosqueUpdated"),
            severity: "success",
          });
          handleCloseAddMosqueModal(); // Close modal on success
        },
        onError: (error) => {
          console.error("Error updating mosque:", error);
          setNotification({
            open: true,
            message: t("mosques.mosqueUpdateFailed"),
            severity: "error",
          });
        },
      });
    } else {
      addMosque.mutate(mosqueData, {
        onSuccess: () => {
          setNotification({
            open: true,
            message: t("mosques.mosqueAdded"),
            severity: "success",
          });
          handleCloseAddMosqueModal(); // Close modal on success
        },
        onError: (error) => {
          console.error("Error adding mosque:", error);
          setNotification({
            open: true,
            message: t("mosques.mosqueAddFailed"),
            severity: "error",
          });
        },
      });
    }
  };
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="dark"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                flexDirection={isMobile ? "column" : "row"}
                justifyContent="space-between"
                alignItems={isMobile ? "flex-start" : "center"}
              >
                <MDTypography variant="h6" color="white">
                  {t("mosques.title")}
                </MDTypography>
                <MDBox
                  display="flex"
                  flexDirection={isMobile ? "column" : "row"}
                  gap={isMobile ? 2 : "10px"}
                  width={isMobile ? "100%" : "auto"}
                >
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleAddMosqueClick}
                    fullWidth={isMobile}
                    sx={{
                      borderRadius: 2,
                      fontSize: 14,
                      padding: "8px 16px",
                      color: "black",
                    }}
                  >
                    {t("mosques.addMosque")}
                  </Button>
                </MDBox>
              </MDBox>
              <MDBox p={3}>
                <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      placeholder={t("mosques.searchPlaceholder")}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                        endAdornment: searchTerm && (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setSearchTerm("")}
                              edge="end"
                              size="small"
                            >
                              <ClearIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>{t("mosques.sortBy")}</InputLabel>
                      <Select
                        value={sortBy}
                        label={t("mosques.sortBy")}
                        onChange={(e) => setSortBy(e.target.value)}
                      >
                        <MenuItem value="name">{t("mosques.fields.mosqueName")}</MenuItem>
                        <MenuItem value="responsibleName">{t("mosques.fields.responsibleName")}</MenuItem>
                        <MenuItem value="city">{t("mosques.fields.city")}</MenuItem>
                        <MenuItem value="createdAt">{t("mosques.fields.createdAt")}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={true}
                  entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                  showTotalEntries
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      {/* Mosque Modal */}
      <Dialog open={isMosqueModalOpen} onClose={handleCloseAddMosqueModal}>
        <DialogTitle>{isEditMode ? t("mosques.editMosque") : t("mosques.addMosque")}</DialogTitle>
        <DialogContent>
          <TextField
            name="name"
            label={t("mosques.fields.mosqueName")}
            value={newMosque.name || ""}
            onChange={handleInputChangeNewMosque}
            fullWidth
            margin="dense"
            required
          />
          <TextField
            name="responsibleName"
            label={t("mosques.fields.responsibleName")}
            value={newMosque.responsibleName || ""}
            onChange={handleInputChangeNewMosque}
            fullWidth
            margin="dense"
            required
          />
          <TextField
            name="mobileNumber"
            label={t("mosques.fields.mobileNumber")}
            value={newMosque.mobileNumber || ""}
            onChange={handleInputChangeNewMosque}
            fullWidth
            margin="dense"
            required
          />
          <TextField
            name="secondNumber"
            label={t("mosques.fields.secondNumber")}
            value={newMosque.secondNumber || ""}
            onChange={handleInputChangeNewMosque}
            fullWidth
            margin="dense"
          />
          <TextField
            name="address"
            label={t("mosques.fields.address")}
            value={newMosque.address || ""}
            onChange={handleInputChangeNewMosque}
            fullWidth
            margin="dense"
            required
            multiline
            rows={2}
            helperText={t("mosques.fields.addressHelp")}
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                name="city"
                label={t("mosques.fields.city")}
                value={newMosque.city || ""}
                onChange={handleInputChangeNewMosque}
                fullWidth
                margin="dense"
                required
                helperText={t("mosques.fields.cityHelp")}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                name="district"
                label={t("mosques.fields.district")}
                value={newMosque.district || ""}
                onChange={handleInputChangeNewMosque}
                fullWidth
                margin="dense"
                helperText={t("mosques.fields.districtHelp")}
              />
            </Grid>
          </Grid>
          <TextField
            name="postalCode"
            label={t("mosques.fields.postalCode")}
            value={newMosque.postalCode || ""}
            onChange={handleInputChangeNewMosque}
            fullWidth
            margin="dense"
            helperText={t("mosques.fields.postalCodeHelp")}
          />
          <FormControlLabel
            control={
              <Switch
                checked={newMosque.isActive}
                onChange={(e) => setNewMosque((prev) => ({ ...prev, isActive: e.target.checked }))}
                color="primary"
              />
            }
            label={t("mosques.fields.isActive")}
            sx={{ marginTop: 1 }}
          />
          <TextField
            name="location.type"
            label={t("mosques.fields.locationType")}
            value={newMosque.location?.type || "Point"}
            onChange={handleInputChangeNewMosque}
            fullWidth
            margin="dense"
            disabled
            required
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                name="x"
                label={t("mosques.fields.latitude")}
                type="number"
                value={newMosque.location?.coordinates[1] || ""} // Ensure index 1 for latitude
                onChange={handleInputChangeNewMosque}
                fullWidth
                margin="dense"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                name="y"
                label={t("mosques.fields.longitude")}
                type="number"
                value={newMosque.location?.coordinates[0] || ""} // Ensure index 0 for longitude
                onChange={handleInputChangeNewMosque}
                fullWidth
                margin="dense"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddMosqueModal} color="secondary">
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSaveMosque} color="primary">
            {isEditMode ? t("common.update") : t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Notification Snackbar */}
      <MDSnackbar
        color={notification.severity}
        icon={notification.severity === "success" ? "check" : "error"}
        title={notification.severity === "success" ? t("common.success") : t("common.error")}
        content={notification.message}
        open={notification.open}
        onClose={handleCloseNotification}
        close={handleCloseNotification}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default Tables;
